import { httpJson, NETWORK_TIMEOUT_MS, withTimeout } from '@/lib/compat'
import { withLanguage } from '@/lib/language'
import type { PlaybackTicket } from '@/lib/playback'
import { authStatus } from '@/lib/session'

/**
 * «Canlı» xətti kanal data qatı (TV).
 *
 * now/schedule anonimdir (mirror `catalog.ts` — timeout + fallback, boş ekran yox).
 * Bilet AUTH tələb edir və 401/403/404 fərqi mənalıdır → `authStatus` (session.ts)
 * işlədilir: `Bearer`-i özü qoyur, bitmiş token-i yeniləyir, status kodunu saxlayır
 * (mirror `playback.ts` `requestTicket`).
 */

export interface LiveProgram {
  id: string
  contentId: string
  contentSlug: string | null
  title: string
  posterUrl: string | null
  backdropUrl: string | null
  startsAt: string
  endsAt: string
  durationSeconds: number
  isLive: boolean
  positionSeconds: number
  startsInSeconds: number
}

/** «İndi nə gedir» — cari veriliş və onun mövqeyi. */
export interface LiveNow {
  channelName: string
  isActive: boolean
  current: LiveProgram | null
  next: LiveProgram | null
  serverTime: string
}

/** Canlı oynatma biletinin nəticəsi. */
export type LivePlaybackResult =
  | { status: 'country' }
  | { status: 'ok'; ticket: PlaybackTicket }
  | { status: 'unauthorized' } // 401 — giriş
  | { status: 'forbidden' } // 403 — abunəlik lazımdır (paywall)
  | { status: 'age' } // 403 — doğum tarixi və ya yaş həddi; abunə həll etmir
  | { status: 'noBroadcast' } // 404 — cədvəldə boşluq
  | { status: 'unavailable' } // manifest yox / şəbəkə

const TIMEOUT_MS = 6000

/*
  ⚠ SORĞU SƏTRİ ƏL İLƏ QURULMUR (2026-08-30): əvvəl bu iki funksiya `'?lang=' + lang`
    yazırdı, kataloq qatı isə `withLanguage()` işlədirdi — yəni EYNİ iş iki cür
    edilirdi və biri dəyişəndə o biri arxada qalırdı (defolt parametr `readLanguage()`
    modul yüklənəndə YOX, çağırışda hesablanır, amma forma yenə də ayrı idi).
    İndi tək yol var; `?`/`&` seçimini də həmin köməkçi edir.
  ⚠ `lang` PARAMETRİ GÖTÜRÜLDÜ: heç bir çağırış yeri onu vermirdi (yalnız defolt
    işlədilirdi) və saxlanılsaydı dil üçün İKİNCİ mənbə olardı.
*/

/** Hazırda gedən verilişi çəkir (server saatına görə). */
export function getLiveNow(): Promise<LiveNow | null> {
  return withTimeout(httpJson<LiveNow>(withLanguage('/api/live/now')), TIMEOUT_MS, 'live/now').catch(() => null)
}

/** Yaxın saatların proqramını çəkir. */
export function getLiveSchedule(): Promise<LiveProgram[]> {
  return withTimeout(httpJson<LiveProgram[]>(withLanguage('/api/live/schedule')), TIMEOUT_MS, 'live/schedule').catch(
    () => [],
  )
}

/** Canlı bilet — kontent cədvəldən (server saatı) gəlir; body yalnız {profileId, deviceId}. */
export function requestLivePlayback(profileId: string): Promise<LivePlaybackResult> {
  const body = JSON.stringify({ profileId, deviceId: null })

  return withTimeout(
    authStatus('/api/playback/live/ticket', { method: 'POST', body }),
    NETWORK_TIMEOUT_MS,
    'playback/live/ticket',
  )
    .then((response): LivePlaybackResult => {
      if (response.status !== 200) return statusResult(response.status, response.text)
      try {
        return okFrom(JSON.parse(response.text))
      } catch {
        return { status: 'unavailable' }
      }
    })
    .catch((): LivePlaybackResult => ({ status: 'unavailable' }))
}

function statusResult(status: number, text: string): LivePlaybackResult {
  if (status === 401) return { status: 'unauthorized' }
  if (status === 403) {
    // 2026-10-04: canlı yaş rəddi paywall kimi göstərilirdi; film bileti ilə eyni qayda.
    try {
      const code = (JSON.parse(text) as { code?: string }).code
      if (code?.startsWith('Playback.Country')) return { status: 'country' }
      if (code === 'Playback.AgeRestricted' || code === 'Playback.ProfileAgeRestricted' ||
          code === 'Playback.BirthDateRequired') return { status: 'age' }
    } catch {
      // Naməlum rədd hüquq rəddi olaraq qalır.
    }
    return { status: 'forbidden' }
  }
  if (status === 404 || status === 204) return { status: 'noBroadcast' }
  return { status: 'unavailable' }
}

function okFrom(data: unknown): LivePlaybackResult {
  const envelope = data as { ticket?: PlaybackTicket } | null
  const ticket = envelope ? envelope.ticket : null
  if (!ticket || !ticket.manifestUrl) return { status: 'unavailable' }
  return { status: 'ok', ticket }
}

/*
  ⚠ NƏBZ (HEARTBEAT) — 2026-09-25, BusinessRequirement 19.6.
    ƏVVƏL veb TV app-ı canlıda nəbz GÖNDƏRMİRDİ: sayt 15 s, iOS/Android 10 s göndərir,
    Samsung/LG izləyiciləri isə sayğacda ümumiyyətlə görünmürdü (Docs/LiveModule.md 3.3).
    İndi oynadıcı canlı bilet alandan sonra hər 15 saniyədə bu funksiyanı çağırır.
    Cavab əhəmiyyətsizdir və uğursuzluq udulur: nəbz kritik deyil, ekranı bloklamamalıdır.
*/
export const LIVE_HEARTBEAT_MS = 15000

/** «İndi izləyirəm» siqnalı — server profil sahibliyini yoxlayıb presence damğasını yeniləyir. */
export function sendLiveHeartbeat(profileId: string): Promise<void> {
  const body = JSON.stringify({ profileId })
  return withTimeout(authStatus('/api/live/heartbeat', { method: 'POST', body }), TIMEOUT_MS, 'live/heartbeat')
    .then(() => undefined)
    .catch(() => undefined)
}
